import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import CognitoService from '../services/cognitoService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const storedUser = localStorage.getItem('pastelyUser');
    const storedDashboard = localStorage.getItem('pastelyDashboard');
    const guestMode = localStorage.getItem('guestMode');
    
    if (guestMode === 'true') {
      setIsGuest(true);
      setUser({
        userId: 'guest',
        name: 'Guest User',
        email: null,
        picture: null,
        subscriptionTier: 'guest'
      });
    } else if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        if (storedDashboard) {
          const parsedDashboard = JSON.parse(storedDashboard);
          setDashboard(parsedDashboard);
        }
      } catch (error) {
        localStorage.removeItem('pastelyUser');
        localStorage.removeItem('pastelyDashboard');
      }
    }
    setLoading(false);
  }, []);

  const loginAsGuest = () => {
    const guestUser = {
      userId: 'guest',
      name: 'Guest User',
      email: null,
      picture: null,
      subscriptionTier: 'guest'
    };
    
    setUser(guestUser);
    setIsGuest(true);
    localStorage.setItem('guestMode', 'true');
  };

  const login = async (credential) => {
    try {
      setLoading(true);
      
      // Clear guest mode when real user logs in
      localStorage.removeItem('guestMode');
      setIsGuest(false);
      
      // Send Google token to backend for verification and user creation
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google`, {
        googleToken: credential
      });
      
      const { userId, name, email, picture, subscriptionTier, dashboard: userDashboard } = response.data;
      
      // Create user object with backend response
      const userProfile = {
        userId,
        email,
        name,
        picture,
        subscriptionTier,
        provider: 'google' // Add provider info
      };
      
      // Store in local storage
      localStorage.setItem('pastelyUser', JSON.stringify(userProfile));
      localStorage.setItem('pastelyDashboard', JSON.stringify(userDashboard));
      localStorage.setItem('pastelyToken', credential);
      localStorage.setItem('userId', userId); // For API calls
      
      // Update state
      setUser(userProfile);
      setDashboard(userDashboard);

      return userProfile;
    } catch (error) {
      
      // If backend is not available, fallback to local JWT decode (for development)
      if (error.code === 'ECONNREFUSED' || error.response?.status >= 500) {
        return await loginFallback(credential);
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // NEW COGNITO LOGIN - DOESN'T INTERFERE WITH GOOGLE
  const loginWithCognito = async (cognitoUserData) => {
    try {
      setLoading(true);
      
      // Use the ID token from Cognito for backend authentication
      const cognitoToken = cognitoUserData.idToken;
      
      // Send Cognito user data to backend for verification and user creation
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/cognito`, {
        cognitoToken: cognitoToken,
        userData: cognitoUserData
      });
      
      const { userId, name, email, picture, subscriptionTier, dashboard: userDashboard } = response.data;
      
      // Create user object with backend response
      const userProfile = {
        userId,
        email,
        name,
        picture,
        subscriptionTier,
        provider: 'microsoft' // Add provider info
      };
      
      // Store in local storage
      localStorage.setItem('pastelyUser', JSON.stringify(userProfile));
      localStorage.setItem('pastelyDashboard', JSON.stringify(userDashboard));
      localStorage.setItem('pastelyToken', cognitoToken);
      localStorage.setItem('userId', userId); // For API calls
      
      // Update state
      setUser(userProfile);
      setDashboard(userDashboard);

      return userProfile;
    } catch (error) {
      
      // Fallback for development
      if (error.code === 'ECONNREFUSED' || error.response?.status >= 500) {
        return await cognitoLoginFallback(cognitoUserData);
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // NEW COGNITO FALLBACK
  const cognitoLoginFallback = async (cognitoUserData) => {
    try {
      // Create user object with Cognito profile info
      const userProfile = {
        userId: cognitoUserData.userId,
        email: cognitoUserData.email,
        name: cognitoUserData.name,
        picture: cognitoUserData.picture,
        subscriptionTier: 'FREE', // Default for fallback
        provider: 'microsoft'
      };
      
      // Create mock dashboard for development
      const mockDashboard = {
        tokensUsed: 0,
        tokenLimit: 10000000,
        storageUsed: 0,
        storageLimit: 10737418240,
        publicSnipsCount: 0,
        privateSnipsCount: 0,
        totalSnipsCount: 0,
        tokenUsagePercentage: 0.0,
        storageUsagePercentage: 0.0,
        storageUsedFormatted: "0 B",
        storageLimitFormatted: "10.0 GB",
        nearTokenLimit: false,
        nearStorageLimit: false,
        subscriptionActive: true
      };
      
      // Store in local storage
      localStorage.setItem('pastelyUser', JSON.stringify(userProfile));
      localStorage.setItem('pastelyDashboard', JSON.stringify(mockDashboard));
      localStorage.setItem('pastelyToken', cognitoUserData.idToken || 'cognito-fallback-token');
      localStorage.setItem('userId', userProfile.userId);
      
      // Update state
      setUser(userProfile);
      setDashboard(mockDashboard);
      
      return userProfile;
    } catch (error) {
      console.error('Cognito fallback login error:', error);
      throw error;
    }
  };

  // Fallback login method for development when backend is unavailable
  const loginFallback = async (credential) => {
    try {
      // Clear guest mode when real user logs in
      localStorage.removeItem('guestMode');
      setIsGuest(false);
      
      const decodedUser = jwtDecode(credential);
      
      // Create user object with Google profile info
      const userProfile = {
        userId: decodedUser.sub,
        email: decodedUser.email,
        name: decodedUser.name,
        picture: decodedUser.picture,
        subscriptionTier: 'FREE', // Default for fallback
        provider: 'google'
      };
      
      // Create mock dashboard for development
      const mockDashboard = {
        tokensUsed: 0,
        tokenLimit: 10000000,
        storageUsed: 0,
        storageLimit: 10737418240,
        publicSnipsCount: 0,
        privateSnipsCount: 0,
        totalSnipsCount: 0,
        tokenUsagePercentage: 0.0,
        storageUsagePercentage: 0.0,
        storageUsedFormatted: "0 B",
        storageLimitFormatted: "10.0 GB",
        nearTokenLimit: false,
        nearStorageLimit: false,
        subscriptionActive: true
      };
      
      // Store in local storage
      localStorage.setItem('pastelyUser', JSON.stringify(userProfile));
      localStorage.setItem('pastelyDashboard', JSON.stringify(mockDashboard));
      localStorage.setItem('pastelyToken', credential);
      localStorage.setItem('userId', userProfile.userId);
      
      // Update state
      setUser(userProfile);
      setDashboard(mockDashboard);
      
      return userProfile;
    } catch (error) {
      console.error('Fallback login error:', error);
      throw error;
    }
  };

  // ENHANCED LOGOUT - HANDLES BOTH PROVIDERS
  const logout = async () => {
    // Sign out from Cognito if user was authenticated via Cognito
    if (user?.provider === 'microsoft') {
      try {
        await CognitoService.signOut();
      } catch (error) {
        // Silent fail for sign out
      }
    }
    
    // Clear local storage
    localStorage.removeItem('pastelyUser');
    localStorage.removeItem('pastelyDashboard');
    localStorage.removeItem('pastelyToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('guestMode'); // Clear guest mode
    
    // Clear state
    setUser(null);
    setDashboard(null);
    setIsGuest(false);
  };

  // Refresh dashboard data from backend
  const refreshDashboard = async () => {
    if (!user?.userId) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/users/${user.userId}/dashboard`
      );
      
      const updatedDashboard = response.data;
      setDashboard(updatedDashboard);
      localStorage.setItem('pastelyDashboard', JSON.stringify(updatedDashboard));
      
      return updatedDashboard;
    } catch (error) {
      return dashboard; // Return current dashboard if refresh fails
    }
  };

  // Check if user can perform an action (for UI feedback)
  const checkUsageLimits = async (tokens = null, storage = null) => {
    if (!user?.userId) return { canUseTokens: true, canUseStorage: true };
    
    try {
      const params = new URLSearchParams();
      if (tokens) params.append('tokens', tokens);
      if (storage) params.append('storage', storage);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/users/${user.userId}/can-use?${params}`
      );
      
      return response.data;
    } catch (error) {
      return { canUseTokens: true, canUseStorage: true }; // Optimistic fallback
    }
  };

  const value = {
    user,
    dashboard,
    loading,
    isGuest,
    login, // Existing Google login
    loginAsGuest, // New guest login
    loginWithCognito, // New Cognito login
    logout,
    refreshDashboard,
    checkUsageLimits,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
